import { useQuery } from '@tanstack/react-query';
import { publicGet } from '@/lib/api/publicClient';
import { publicReviews } from '@/lib/api/endpoints';

/**
 * Mirrors the shape of `ReviewPlatform` enum exposed by the operations
 * Prisma client. Uppercase string literals — no runtime dependency on
 * Prisma in the frontend.
 */
export type ReviewPlatformId = 'GOOGLE' | 'FACEBOOK' | 'YELP' | 'BBB' | 'THUMBTACK';

/**
 * Mirrors `ReviewLandingPayload` exported by
 * `apps/operations/src/modules/reviews/interfaces/review.interfaces.ts`.
 */
export interface ReviewLandingPayload {
  appointmentId: string;
  /**
   * Highest rating that still shows the recovery-worded private capture.
   * `0` means the tenant runs no recovery band at all.
   *
   * Optional on purpose. This interface is a hand-maintained mirror of an
   * unauthenticated wire contract with no runtime validation, and the field
   * only ever selects *copy* on the last section of the landing page — never
   * whether the stars, the platform chooser or the private capture mount, and
   * never their order. Read it as `serviceRecoveryThreshold ?? 3` so a payload
   * that predates the rename degrades to wrong wording rather than to a
   * control the customer cannot reach.
   */
  serviceRecoveryThreshold?: number;
  submissionStatus: 'OPEN' | 'COMPLETED';
  contactName?: string;
  serviceLabel?: string;
  technicianName?: string;
  appointmentCompletedAt?: string;
  branding: {
    tenantName: string;
    logoUrl?: string;
    primaryColor?: string;
  };
  publicPlatforms: Array<{
    platform: ReviewPlatformId;
    deepLinkUrl: string;
  }>;
}

interface RawLandingResponse {
  data: ReviewLandingPayload;
}

/**
 * Resolves a review-request token to its landing payload. Errors (invalid
 * / expired token) flow through `isError` so the route renders the
 * expired state.
 */
export function useReviewLanding(token: string) {
  return useQuery<ReviewLandingPayload>({
    queryKey: ['public', 'review', 'landing', token],
    queryFn: async () => {
      const raw = await publicGet<RawLandingResponse>(publicReviews.landing(token));
      // Backend wraps the payload in `{ data }`. Unwrap so consumers don't
      // have to know.
      return raw.data;
    },
    retry: false,
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}
